import classnames from 'classnames';
import _ from 'lodash';
import React, {Component} from 'react';
import {setBehaviourVariables} from '../actions';

const INCOMPLETE_NUMBER = /^[-+]?\.$|^[-+]?$/;

function numberInputsFromVariables(variables) {
  return _.mapValues(variables, (v) => (v === undefined || v === null ? '' : String(v)));
}

class VariablesForm extends Component {
  constructor(props) {
    super(props);

    // Local state separate from app state to support edit form.
    this.state = {
      form_open: !!props.start_open,
      variables: { ...props.variables },
      numberInputs: numberInputsFromVariables(props.variables),
      errors: {},
      pulsate_errors: false
    };

    this.handleNumberInputChange = this.handleNumberInputChange.bind(this);
    this.handleEnumInputChange = this.handleEnumInputChange.bind(this);
    this.handleOpenForm = this.handleOpenForm.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.resetVariables = this.resetVariables.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data_set !== this.props.data_set) {
      this.setState((state, props) => ({
        form_open: false,
        variables: { ...props.variables },
        numberInputs: numberInputsFromVariables(props.variables),
        errors: {}
      }));
    }
  }

  handleOpenForm(event) {
    this.setState({ form_open: true });
    this.resetVariables();
    event.preventDefault();
  }

  metaDataForVariables(variables) {
    const { meta_data, getMetaData } = this.props;
    return getMetaData ? getMetaData(variables) : meta_data;
  }

  activeMetaData() {
    const sourceVariables = this.state.form_open ? this.state.variables : this.props.variables;
    return this.metaDataForVariables(sourceVariables);
  }

  handleNumberInputChange(event, fieldMeta) {
    const { name, value: raw } = event.target;
    this.setState((state) => {
      const numberInputs = { ...state.numberInputs, [name]: raw };
      const new_vars = { ...state.variables };
      const new_errors = { ...state.errors };

      if (raw === '' || INCOMPLETE_NUMBER.test(raw)) {
        new_errors[name] = '';
        return { numberInputs, variables: new_vars, errors: new_errors };
      }

      const numValue = Number(raw);
      if (Number.isNaN(numValue)) {
        new_errors[name] = 'Invalid number';
      } else if (fieldMeta && typeof fieldMeta.min !== 'undefined') {
        new_vars[name] = numValue;
        if (numValue < fieldMeta.min) {
          new_errors[name] = 'Min: ' + fieldMeta.min;
        } else if (numValue > fieldMeta.max) {
          new_errors[name] = 'Max: ' + fieldMeta.max;
        } else {
          new_errors[name] = '';
        }
      } else {
        new_vars[name] = numValue;
        new_errors[name] = '';
      }
      return { numberInputs, variables: new_vars, errors: new_errors };
    });
  }

  handleEnumInputChange(event) {
    const target = event.target;
    const value = isNaN(target.value) ? target.value : Number(target.value);
    this.setState((state) => {
      const newVars = this.props.adjustVariables
        ? this.props.adjustVariables(target.name, value, state.variables)
        : { ...state.variables, [target.name]: value };
      return { variables: newVars };
    });
  }

  handleCancel(event) {
    this.setState({ form_open: false });
    this.resetVariables();
    event.preventDefault();
  }

  handleSubmit(event) {
    const meta_data = this.activeMetaData().filter(m => !m.calculated && !m.readonly && !m.enum);
    const errors = { ...this.state.errors };
    const variables = { ...this.state.variables };
    const { numberInputs = {} } = this.state;

    meta_data.forEach((m) => {
      const raw = Object.prototype.hasOwnProperty.call(numberInputs, m.key)
        ? numberInputs[m.key]
        : String(variables[m.key]);
      if (raw === '' || INCOMPLETE_NUMBER.test(raw)) {
        errors[m.key] = 'Required';
        return;
      }
      const numValue = Number(raw);
      if (Number.isNaN(numValue)) {
        errors[m.key] = 'Invalid number';
      } else if (typeof m.min !== 'undefined' && numValue < m.min) {
        errors[m.key] = 'Min: ' + m.min;
      } else if (typeof m.max !== 'undefined' && numValue > m.max) {
        errors[m.key] = 'Max: ' + m.max;
      } else {
        variables[m.key] = numValue;
        errors[m.key] = '';
      }
    });

    if (_.some(_.values(errors), (e) => !!e)) {
      this.setState({ errors, variables, pulsate_errors: true });
      setTimeout(() => {this.setState({ pulsate_errors: false });}, 2000);
    } else {
      this.props.dispatch(setBehaviourVariables(this.props.data_set, variables));
      this.setState({ form_open: false });
    }
    event.preventDefault();
  }

  resetVariables() {
    this.setState((state, props) => ({
      variables: { ...props.variables },
      numberInputs: numberInputsFromVariables(props.variables),
      errors: {}
    }));
  }

  handleKeyDown(event) {
    // 'keypress' event misbehaves on mobile so we track 'Enter' key via 'keydown' event
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.handleSubmit(event);
    }
  }

  render() {
    const { form_open } = this.state;
    const saved = this.props.variables;
    const local = this.state.variables;
    const { errors, pulsate_errors } = this.state;
    const meta_data = this.activeMetaData();
    let notes = [];

    return <div className="c-form__variables-panel">
      <div className="c-form__variables-panel-inset">
        <div className="c-form__variables-panel-heading">
          <div>Calculation Variables</div>
        </div>
        <div className="c-form__variables-wrapper">
          <div className="c-form__variables">
            {meta_data.filter(m => !(m.readonly && saved[m.key] === undefined)).map(m => {
                let noteKey = '';
                if (m.note) {
                  notes.push(m.note);
                  noteKey = m.note ? m.note.substring(0, 1) : '';
                }
                const gridStyle = { gridRowStart: (m.row || 1) };
                if (m.col) {
                  gridStyle.gridColumnStart = m.col;
                }
                return (
                  <div key={m.key} style={gridStyle}>
                    <span className="c-form__label_readonly">{m.displayName}<span className="c-form__note-key">{noteKey}</span>:</span>
                    <span className="c-form__value_readonly">{this.renderValue(m, saved)}</span>
                  </div>);
              }
            )}
          </div>
          <div className="graph-panel-actions">
            {this.props.panelActions}
            <div className="c-button c-form__edit-button" onClick={this.handleOpenForm} title="Edit">
              <i className="fa fa-edit"/><label>Edit</label>
            </div>
          </div>
        </div>

        <div className="c-form__notes">
          {notes.map(n => {
              const noteKey = n.substring(0, 1);
              const note = n.substring(1);
              return <div key={noteKey}><span className="c-form__note-key">{noteKey}</span>{note}</div>;
            }
          )}
        </div>
      </div>
      {form_open &&
       <div className="c-form__variables-form-holder">
         <div className="c-form__variables-form">
           {_.sortBy(meta_data.filter(m => !m.calculated && !m.readonly), 'order').map(m =>
             <div key={m.key}>
               <label>{m.editDisplayName || m.displayName}: </label>
               {m.enum && this.renderEnumField(m, local, errors, pulsate_errors)}
               {!m.enum && this.renderNumberField(m, local, errors, pulsate_errors)}
             </div>
           )}
           <div className="c-form__buttons">
             <a className="c-form__cancel-button" onClick={this.handleCancel}>
               <i className="fa fa-times-circle"/><span>Cancel</span>
             </a>
             <a className="c-button c-form__submit-button" onClick={this.handleSubmit}>
               <i className="fa fa-check-square"/><span>Submit</span>
             </a>
           </div>
         </div>
       </div>
      }
    </div>;
  }

  renderNumberField(m, local, errors, pulsate_errors) {
    const displayValue = Object.prototype.hasOwnProperty.call(this.state.numberInputs, m.key)
      ? this.state.numberInputs[m.key]
      : String(local[m.key]);
    return <span>
    <input name={m.key} type="text" inputMode="decimal" value={displayValue}
           onChange={(e) => this.handleNumberInputChange(e, m)} onKeyDown={this.handleKeyDown}/>
      {errors[m.key] &&
       <span className={classnames('c-form__error-messsage', pulsate_errors ? ' pulsate' : '')}>{errors[m.key]}</span>
      }
    </span>;
  }

  renderEnumField(m, local, errors, pulsate_errors) {
    const includeBlank = !_.values(m.enum).includes(local[m.key]);
    return <span>
    <select name={m.key} value={local[m.key]} onChange={this.handleEnumInputChange}>
        {includeBlank && <option key={-1} value={-1}>-- Select One --</option>}
      {
        _.map(_.toPairs(m.enum), (pair) => <option key={pair[1]} value={pair[1]}>{pair[0]}</option>)
      }
    </select>
      {errors[m.key] &&
       <span className={classnames('c-form__error-messsage', pulsate_errors ? ' pulsate' : '')}>{errors[m.key]}</span>
      }
    </span>;
  }

  renderValue(m, saved) {
    if (m.enum) {
      const value = saved[m.key];
      return _.findKey(m.enum, v => v === value);
    } else {
      return saved[m.key];
    }
  }
}

export default VariablesForm;



// WEBPACK FOOTER //
// ./src/components/VariablesForm.js